var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { injectable } from 'inversify';
import queryString from 'query-string';
import escape from 'lodash/escape';
let QueryService = class QueryService {
    /**
     * Преобразовать объект в query string (ключи предварительно сортируются)
     * При наличии у объекта свойства 'filters' типа object, развернуть каждое его поле дополнительно
     *
     * @param query - Объект
     * @param namespace - Префикс для параметров и фильтров
     * @returns Query string
     */
    stringify(query, namespace = '') {
        const _a = query, { filters } = _a, rest = __rest(_a, ["filters"]);
        const stringifyRest = Object.entries(rest).reduce((acc, [key, value]) => {
            if (!namespace || key.includes('_param_') || key.includes('_filter_')) {
                return Object.assign(Object.assign({}, acc), { [key]: value });
            }
            return Object.assign(Object.assign({}, acc), { [`${namespace}_param_${key}`]: value });
        }, {});
        if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
            return queryString.stringify(stringifyRest, {
                arrayFormat: 'bracket',
            });
        }
        return queryString.stringify(Object.assign(Object.assign({}, stringifyRest), Object.entries(filters).reduce((acc, [key, value]) => {
            return Object.assign(Object.assign({}, acc), { [namespace ? `${namespace}_filter_${key}` : `filter_${key}`]: value });
        }, {})), { arrayFormat: 'bracket' });
    }
    /**
     * Распарсить query string и преобразовать ее к типу объект
     * При этом поля, начинающиеся с 'filter_', будут объедены в поле 'filters' результативного объекта.
     * При этом символы в исходной строке будут конвертированы к соответствующим HTML-эквивалентам
     * ```
     * “&“, “", ">“, '“', “'“, “`”
     * ```
     *
     * @typeParam T - Тип объекта
     * @param query - Строка, ожидающая преобразования
     * @param namespace - Префикс для параметров и фильтров
     * @returns Объект типа T с опциональным полем filters
     */
    parse(query, namespace = '') {
        const entries = queryString.parse(query, { arrayFormat: 'bracket' });
        const PROPERTY_SEPARATOR = '.';
        const result = {};
        if (entries) {
            Object.entries(entries).forEach(([key, value]) => {
                let escapedValue = '';
                if (value && Array.isArray(value)) {
                    escapedValue = value.map((fragment) => escape(fragment ? fragment : undefined));
                }
                else if (value && typeof value === 'string') {
                    escapedValue = escape(value);
                }
                if (!escapedValue) {
                    return;
                }
                if (namespace) {
                    if (!key.startsWith(`${namespace}_filter_`) &&
                        !key.startsWith(`${namespace}_param_`)) {
                        return;
                    }
                }
                if (key.startsWith(namespace ? `${namespace}_filter_` : 'filter_')) {
                    key = key.substring((namespace ? `${namespace}_filter_` : 'filter_').length);
                    if (!result.filters)
                        result.filters = {};
                    if (key.includes(PROPERTY_SEPARATOR)) {
                        const props = key.split(PROPERTY_SEPARATOR);
                        //eslint-disable-next-line no-magic-numbers
                        if (props.length > 2 || !['start', 'end'].includes(props[1])) {
                            return;
                        }
                        let leaf = result.filters;
                        for (let i = 0; i < props.length - 1; i++) {
                            if (!leaf[props[i]])
                                leaf[props[i]] = {};
                            leaf = leaf[props[i]];
                        }
                        leaf[props[props.length - 1]] = escapedValue;
                    }
                    else {
                        result.filters[key] = value;
                    }
                }
                else if (key.startsWith(`${namespace}_param_`)) {
                    key = key.substring(`${namespace}_param_`.length);
                    result[key] = value;
                }
                else {
                    result[key] = value;
                }
            });
        }
        return result;
    }
};
QueryService = __decorate([
    injectable()
], QueryService);
export { QueryService };
